import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Cropper from 'react-easy-crop';
import { useTranslation } from 'react-i18next';
import './ImageCropper.css';

const ImageCropper = ({ image, onCropComplete, onCancel, aspectRatio = 1 }) => {
    const { t } = useTranslation();
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [rotation, setRotation] = useState(0);

    const onCropAreaComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleConfirm = useCallback(() => {
        if (croppedAreaPixels) {
            onCropComplete(croppedAreaPixels, rotation);
        }
    }, [croppedAreaPixels, rotation, onCropComplete]);

    return (
        <div className="image-cropper-overlay">
            <div className="image-cropper-container">
                {/* Header */}
                <div className="image-cropper-header">
                    <h3>{t('profile.cropImage')}</h3>
                    <button
                        className="btn-cropper-close"
                        onClick={onCancel}
                        aria-label={t('common.close')}
                    >
                        ✕
                    </button>
                </div>

                {/* Cropper Area */}
                <div className="image-cropper-content">
                    <Cropper
                        image={image}
                        crop={crop}
                        zoom={zoom}
                        rotation={rotation}
                        aspect={aspectRatio}
                        onCropChange={setCrop}
                        onCropComplete={onCropAreaComplete}
                        onZoomChange={setZoom}
                        onRotationChange={setRotation}
                        cropShape="round"
                        showGrid={false}
                        minZoom={0.5}
                        maxZoom={5}
                    />
                </div>

                {/* Controls */}
                <div className="image-cropper-controls">
                    {/* Zoom Slider */}
                    <div className="control-group">
                        <label htmlFor="zoom-slider">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"></circle>
                                <path d="m21 21-4.35-4.35"></path>
                                <line x1="11" y1="8" x2="11" y2="14"></line>
                                <line x1="8" y1="11" x2="14" y2="11"></line>
                            </svg>
                            <span>{t('profile.zoom')}</span>
                        </label>
                        <div className="slider-container">
                            <span className="slider-label">-</span>
                            <input
                                id="zoom-slider"
                                type="range"
                                min={0.5}
                                max={5}
                                step={0.1}
                                value={zoom}
                                onChange={(e) => setZoom(parseFloat(e.target.value))}
                                className="slider"
                            />
                            <span className="slider-label">+</span>
                        </div>
                    </div>

                    {/* Rotation Slider */}
                    <div className="control-group">
                        <label htmlFor="rotation-slider">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"></path>
                            </svg>
                            <span>{t('profile.rotation')}</span>
                        </label>
                        <div className="slider-container">
                            <span className="slider-label">↺</span>
                            <input
                                id="rotation-slider"
                                type="range"
                                min={0}
                                max={360}
                                step={1}
                                value={rotation}
                                onChange={(e) => setRotation(parseInt(e.target.value))}
                                className="slider"
                            />
                            <span className="slider-label">↻</span>
                        </div>
                        <span className="rotation-value">{rotation}°</span>
                    </div>
                </div>

                {/* Instructions */}
                <div className="image-cropper-instructions">
                    <div className="instruction-item">
                        <span>{t('profile.cropMobile')}</span>
                    </div>
                    <div className="instruction-item">
                        <span>{t('profile.cropDesktop')}</span>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="image-cropper-actions">
                    <button
                        className="btn btn-cancel"
                        onClick={onCancel}
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        className="btn btn-accept"
                        onClick={handleConfirm}
                    >
                        {t('profile.applyCrop')}
                    </button>
                </div>
            </div>
        </div>
    );
};

ImageCropper.propTypes = {
    image: PropTypes.string.isRequired,
    onCropComplete: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    aspectRatio: PropTypes.number,
};

export default ImageCropper;

